import React, {useState, useEffect} from "react";
import {Card} from 'react-bootstrap';
import {Link, useNavigate} from 'react-router-dom';
import {apiClient} from '../api/ApiClient';
import useFastPhotos from '../fastPhotos/useFastPhotos';
import './ViewsComponent.css';
import Badge from "react-bootstrap/Badge";
import PaginationComponent from "../pagingResults/PaginationComponent";
import { Trash2 } from 'lucide-react';

const ViewsComponent = () => {
  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalElements, setTotalElements] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(0);
  
  const navigate = useNavigate();
  const {fastPhotos, loading: fastPhotosLoading, error: fastPhotosError} = useFastPhotos();

  // Function to record profile view
  const recordProfileView = async (memberId) => {
    try {
      console.log('Recording view for profile ID:', memberId);
      
      const response = await apiClient.post('/rest/recordView', {
        viewedMemberId: memberId
      });
      
      console.log('View recorded response:', response.data);
    } catch (error) {
      console.error('Error recording profile view:', error);
    }
  };

  // Handle click on a profile
  const handleProfileClick = (e, memberId) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    if (token) {
      // Record the profile view
      recordProfileView(memberId);
      // Navigate to the profile page
      navigate(`/profile/view/1/${memberId}`);
    } else {
      navigate('/login');
    }
  };

  // Handle click on a close match profile
  const handleCloseMatchClick = (e, memberId) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    if (token) {
      // Record the profile view
      recordProfileView(memberId);
      // Navigate to the profile page
      navigate(`/profile/view/1/${memberId}`);
    } else {
      navigate('/login');
    }
  };

  // Updated to work with new ViewsEndpoints API
  const handleDeleteView = async (viewId) => {
    try {
      // New API format matching ViewsEndpoints.java
      const viewRequest = {
        view: [
          {
            name: "view_delete_list",
            val: viewId.toString()
          }
        ]
      };
      
      const response = await apiClient.post('/rest/deleteViews', viewRequest);
      
      if (response.status === 200) {
        console.log('View deleted successfully:', response.data);
        // Refresh data after deletion
        fetchViews();
      }
    } catch (error) {
      console.error('Error removing view:', error);
    }
  };

  const fetchViews = async () => {
    setLoading(true);
    try {
      // Use the updated paginated endpoint
      const response = await apiClient.get(`/rest/views?page=${currentPage}&size=${itemsPerPage}`);
      
      if (response.status === 200) {
        console.log('Views response:', response.data);
        
        // Handle Spring Boot Page object
        if (response.data.content) {
          // If the response is a Page object
          setViews(response.data.content);
          setTotalElements(response.data.totalElements);
        } else {
          // Fallback for backward compatibility
          const validData = response.data.filter(item => Array.isArray(item) && item.length > 1);
          setViews(validData);
          setTotalElements(validData.length);
        }
      } else {
        setError('Failed to fetch views');
      }
    } catch (error) {
      console.error('Error fetching views:', error);
      setError('Error fetching views. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('Fetching views - Page:', currentPage, 'Size:', itemsPerPage);
    fetchViews();
  }, [currentPage, itemsPerPage]);

  const formatDate = (dateString) => {
    const options = {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div className="container-1024">
      <div className="results-area">
        <h3>My Profile Views</h3>
        <div className="results-content">
          <div className="results-center-details">
            <div className="w-100 mb-3">
              <PaginationComponent
                totalItems={totalElements}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(newSize) => {
                  setItemsPerPage(newSize);
                  setCurrentPage(0); // Reset to first page when changing size
                }}
              />
            </div>

            {loading ? (
              <p>Loading views...</p>
            ) : error ? (
              <p>{error}</p>
            ) : (
              <div className="card-grid">
                {views.length > 0 ? (
                  views.map((view, index) => (
                    <Card key={index} className="favorite-card position-relative">
                      <div className="delete-icon-wrapper"
                        style={{
                          position: 'absolute',
                          top: '10px',
                          right: '10px',
                          zIndex: 1,
                          cursor: 'pointer',
                          background: 'rgba(255, 255, 255, 0.8)',
                          borderRadius: '50%',
                          padding: '5px'
                        }}>
                        <Trash2
                          size={20}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDeleteView(view[0]);
                          }}
                        />
                      </div>
                      <a 
                        href={`/profile/view/1/${view[1]}`} 
                        className="favorite-card-link"
                        onClick={(e) => handleProfileClick(e, view[1])}
                      >
                        <Card.Img 
                          variant="top" 
                          src={`https://pics.netfixup.com/${view[22]}/${view[21]}`} 
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://pics.netfixup.com/nfu_default_img/addNewUserPhoto.jpg";
                          }}
                        />
                      </a>
                      <Card.Body>
                        <Card.Title>{view[2]}</Card.Title>
                        <Card.Text>
                          <b>Age:</b> {view[3]}<br />
                          <b>Gender:</b> {view[5]}<br />
                          <b>Looking for:</b> {view[6]}<br />
                          <b>Location:</b> {view[17]}, {view[18]}<br />
                          <b>Online: </b> {view[25]}<br />
                          <b>Viewed Date: </b> {formatDate(view[24])}<br />
                        </Card.Text>
                      </Card.Body>
                    </Card>
                  ))
                ) : (
                  <p>No Views Found</p>
                )}
              </div>
            )}
          </div>

          <div className="results-right-sidebar">
            <h5>Close Matches</h5>
            {fastPhotosLoading ? (
              <p>Loading...</p>
            ) : fastPhotosError ? (
              <p>{fastPhotosError}</p>
            ) : fastPhotos.length > 0 ? (
              fastPhotos.map((photo, index) => (
                <Card key={index} className="fast-photo-card-side">
                  <a 
                    href={`/profile/view/1/${photo[0]}`}
                    onClick={(e) => handleCloseMatchClick(e, photo[0])}
                  >
                    <Card.Img 
                      variant="top" 
                      src={`https://pics.netfixup.com/${photo[7]}${photo[6]}`}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://pics.netfixup.com/nfu_default_img/addNewUserPhoto.jpg";
                      }}
                    />
                  </a>
                  <Badge pill bg="light" text="dark">
                    {photo[2]}
                  </Badge>
                </Card>
              ))
            ) : (
              <p>No Fast Photos Found</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ViewsComponent;